/**
 * CookieBanner — le bandeau de consentement du site public.
 *
 * Ce qu'il fait, et surtout ce qu'il ne fait pas :
 *
 *  · Il n'apparaît qu'à la PREMIÈRE visite du site public, tant qu'aucun
 *    choix n'est enregistré. Un choix (accepter ou refuser) le referme
 *    pour six mois.
 *  · Il ne s'affiche PAS dans l'app connectée : le chrome de l'app n'est
 *    pas une surface d'acquisition et le visiteur y a déjà répondu.
 *  · Deux boutons, MÊME taille, MÊME poids typographique, MÊME contraste
 *    perçu : « Accepter » et « Refuser ». Aucun dark pattern — pas de
 *    refus caché dans un lien gris, pas de croix qui vaut acceptation,
 *    pas de bandeau qui revient à chaque page tant qu'on n'a pas accepté.
 *  · Il ne charge rien lui-même. C'est `lib/consent.js` qui décide, après
 *    coup, si les scripts enregistrés partent.
 *
 * Fermer sans choisir n'est pas proposé : il n'y a pas de croix. C'est
 * volontaire — une fermeture sans réponse laisserait un état ambigu. En
 * revanche, l'absence de réponse (visiteur qui s'en va) vaut refus : rien
 * n'est chargé tant que le bouton « Accepter » n'a pas été cliqué.
 *
 *  · Il parle la langue du site. Le texte était écrit en dur en français
 *    dans le JSX : un visiteur néerlandophone recevait la demande de
 *    consentement en français, mot pour mot, y compris les deux boutons.
 *    Un consentement e-Privacy donné dans une langue que la personne n'a
 *    pas choisie n'est pas un consentement éclairé — et c'est la toute
 *    première interaction de chaque visiteur flamand. Les cinq chaînes
 *    vivent désormais dans `i18n/cookieBanner.json`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CONSENT_EVENT,
  acceptConsent,
  denyConsent,
  needsConsentDecision,
} from '../lib/consent';
import { useSiteLanguage } from '../hooks/useSiteLanguage';
import { useCookieBannerT } from '../hooks/useCookieBannerT';

/**
 * Préfixes de routes qui ne sont PAS le site public : l'app connectée,
 * les portails avocat et admin, la cinématique, les écrans de paiement.
 * Le bandeau ne s'y monte pas.
 *
 * Liste explicite plutôt que « est-ce que l'utilisateur est connecté » :
 * un client connecté qui revient sur la home est bien sur le site public
 * et doit pouvoir répondre au bandeau ; inversement, l'app connectée n'a
 * pas à faire surgir un bandeau par-dessus un dossier.
 */
const APP_PREFIXES = [
  '/dashboard', '/cases', '/documents', '/upload', '/mon-juriste',
  '/lawyers', '/contract-guard', '/settings', '/chat', '/payment',
  '/analyze', '/video-call', '/internal', '/admin', '/account',
  '/attorney', '/m/scan', '/attorney-call-confirmed',
  // Le Poste Juriste est un outil interne, derrière un rôle : aucun
  // consentement à demander, et surtout pas un bandeau fixe par-dessus le
  // bouton « Approuver & envoyer » de la File — c'est ce qu'il faisait.
  '/jurist',
];

/**
 * `/attorneys` (l'équipe) et `/attorneys/:slug` (profil public) SONT
 * publics — seules les routes du portail avocat ne le sont pas. Le
 * préfixe '/attorney' ci-dessus attrape aussi '/attorneys' : on rattrape
 * ici les deux pages publiques qui commencent pareil.
 */
const PUBLIC_ATTORNEY_EXCEPTIONS = (path) => {
  if (!path.startsWith('/attorneys')) return false;
  const rest = path.slice('/attorneys'.length).replace(/^\//, '');
  const portal = ['join', 'login', 'dashboard', 'desk', 'inbox', 'my-cases',
    'completed', 'cases', 'earnings', 'live-counsel', 'profile', 'onboarding'];
  return !portal.some((p) => rest === p || rest.startsWith(`${p}/`));
};

export function isPublicSiteRoute(pathname) {
  const path = String(pathname || '/');
  if (PUBLIC_ATTORNEY_EXCEPTIONS(path)) return true;
  return !APP_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

export default function CookieBanner() {
  const { pathname } = useLocation();
  const [lang] = useSiteLanguage();
  const t = useCookieBannerT(lang);
  const [decided, setDecided] = useState(true);

  useEffect(() => {
    // En PRÉ-RENDU (scripts/prerender.mjs), le bandeau reste absent : le
    // HTML capturé doit être celui du premier rendu client, que React
    // hydrate. Un bandeau présent côté serveur et absent au premier rendu
    // client faisait repeindre toute la page.
    if (typeof window !== 'undefined' && window.__ARCHER_PRERENDER__) return undefined;
    setDecided(!needsConsentDecision());
    // « Gérer mes cookies » efface le choix et émet l'événement : le
    // bandeau se rouvre sans rechargement de page.
    const onChange = () => setDecided(!needsConsentDecision());
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  const onAccept = useCallback(() => { acceptConsent(); setDecided(true); }, []);
  const onDeny = useCallback(() => { denyConsent(); setDecided(true); }, []);

  const visible = !decided && isPublicSiteRoute(pathname);
  if (!visible) return null;

  // Les deux boutons partagent CE style. La seule différence est la
  // couleur de fond ; le reste (taille, graisse, rayon, hauteur de frappe)
  // est identique par construction, pas par discipline.
  const btnBase = {
    // `1 1 0` et non `1 1 auto` : sur `auto`, la base est la largeur du
    // texte, donc « Accepter » sortait 10 px plus large que « Refuser ».
    // Dix pixels, c'est déjà un pouce sur la balance — les deux boutons
    // doivent être strictement de même taille.
    flex: '1 1 0',
    minWidth: 0,
    minHeight: 44,
    padding: '11px 22px',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    lineHeight: 1.2,
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'background .15s, border-color .15s',
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('aria')}
      data-testid="cookie-banner"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        // Au-dessus de la barre de gestes iOS.
        bottom: 'max(16px, env(safe-area-inset-bottom))',
        zIndex: 1400,
        maxWidth: 720,
        margin: '0 auto',
        background: '#ffffff',
        border: '1px solid #ebebeb',
        borderRadius: 14,
        boxShadow: '0 20px 50px -18px rgba(15, 23, 42, .35)',
        padding: '18px 20px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <p
        style={{
          flex: '1 1 320px',
          minWidth: 0,
          margin: 0,
          fontSize: 13.5,
          lineHeight: 1.6,
          color: '#374151',
        }}
      >
        {t('body')}{' '}
        <Link
          to="/cookies"
          style={{ color: '#1a56db', textDecoration: 'underline', textUnderlineOffset: 2 }}
          data-testid="cookie-banner-policy-link"
        >
          {t('policyLink')}
        </Link>
      </p>

      <div style={{ flex: '0 1 300px', display: 'flex', gap: 10, minWidth: 0 }}>
        <button
          type="button"
          onClick={onDeny}
          data-testid="cookie-banner-deny"
          style={{ ...btnBase, background: '#f4f4f1', color: '#111827', border: '1px solid #e2e0db' }}
        >
          {t('deny')}
        </button>
        <button
          type="button"
          onClick={onAccept}
          data-testid="cookie-banner-accept"
          style={{ ...btnBase, background: '#1a56db', color: '#ffffff', border: '1px solid #1a56db' }}
        >
          {t('accept')}
        </button>
      </div>
    </div>
  );
}
